import moment from "moment";
import { Question } from "./dto/Question";

export class Assessor {
  constructor(
    public readonly id: string,
    public readonly first_name: string,
    public readonly last_name: string,
    public readonly options: any,
    public readonly company: string | null,
    public readonly email: string | null,
    public readonly phone: string | null,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  isEnabled(): boolean {
    return this.options?.enabled !== "false";
  }

  static fromJson(json: any): Assessor {
    return new Assessor(
      json["id"],
      json["first_name"],
      json["last_name"],
      json["options"] ? (Array.isArray(json["options"]) ? {} : json["options"]) : {},
      json["company"] ?? null,
      json["email"] ?? null,
      json["phone"] ?? null,
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export type CategoryScoringMap = {
  [category: string]: {
    classification: string;
    color: string;
    fromScore: number;
  }[];
};

export const AdminUserRole = "admin";
export const AssessorUserRole = "assessor";
export const LeadAssessorUserRole = "lead-assessor";
export const CustomerUserRole = "customer";
export const PublicApiRole = "public-api";
export interface UserRole {
  id: string;
  name: string;
  containerId: string;
  description: string;
  composite: boolean;
  clientRole: boolean;
}
export class User {
  constructor(
    public readonly id: string,
    public readonly username: string,
    public readonly first_name: string,
    public readonly last_name: string,
    public readonly email: string,
    public readonly enabled: boolean,
    private readonly _roles: UserRole[],
    public readonly companies: string[],
    public readonly question_sets: string[],
    public readonly projects: string[],
  ) {}

  static fromJson(json: any): User {
    return new User(
      json["id"],
      json["username"],
      json["firstName"] ?? null,
      json["lastName"] ?? null,
      json["email"] ?? null,
      json["enabled"],
      json["roles"],
      json["companies"],
      json["question_sets"],
      json["projects"],
    );
  }

  get role(): UserRole | null {
    const adminRole = this.roles.find((role) => role.name === AdminUserRole);
    const leadAssessorRole = this.roles.find((role) => role.name === LeadAssessorUserRole);
    const customerRole = this.roles.find((role) => role.name === CustomerUserRole);
    const assessorRole = this.roles.find((role) => role.name === AssessorUserRole);
    const publicApiRole = this.roles.find((role) => role.name === PublicApiRole);
    return adminRole ?? leadAssessorRole ?? assessorRole ?? customerRole ?? publicApiRole ?? null;
  }

  get roles(): UserRole[] {
    return this._roles.filter((role) =>
      [
        AdminUserRole,
        AssessorUserRole,
        LeadAssessorUserRole,
        CustomerUserRole,
        PublicApiRole,
      ].includes(role.name),
    );
  }

  get isAdmin(): boolean {
    return this.roles.some((r) => r.name === AdminUserRole);
  }

  get isApiUser(): boolean {
    return this.roles.some((r) => r.name === PublicApiRole);
  }
}

export class Company {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly alias: string,
    public readonly logo: string | null,
    public readonly colorPalette: string | null,
    public readonly availableQuestionSetIds: string[],
    public readonly scoringOverride: CategoryScoringMap | null,
    public readonly isArchived: boolean,
    public readonly childCompanies: Company[],
    public readonly parentCompanies: Company[],
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): Company {
    return new Company(
      json["id"],
      json["name"],
      json["alias"],
      json["logo"],
      json["color_palette"],
      json["available_question_set_ids"] ?? [],
      json["override_category_scores"]?.["scoring_per_score_category"] ?? null,
      json["is_archived"] ?? false,
      json["child_companies"]
        ? json["child_companies"].map((item: any) => this.fromJson(item))
        : [],
      json["parent_companies"]
        ? json["parent_companies"].map((item: any) => this.fromJson(item))
        : [],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export class Project {
  constructor(
    public readonly id: string,
    public readonly companyId: string,
    public readonly name: string,
    public readonly logo: string | null,
    public readonly contactName: string | null,
    public readonly contactEmail: string | null,
    public readonly contactPhone: string | null,
    public readonly contactExtra: string | null,
    public readonly scoringOverride: CategoryScoringMap | null,
    public readonly isArchived: boolean,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): Project {
    return new Project(
      json["id"],
      json["company_id"],
      json["name"],
      json["logo"],
      json["contact_name"],
      json["contact_email"],
      json["contact_phone"],
      json["contact_extra"],
      json["override_category_scores"]?.["scoring_per_score_category"] ?? null,
      json["is_archived"] ?? false,
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export class Exam {
  public readonly status: "Concept" | "In uitvoering" | "Afgerond" | "Gearchiveerd";
  public readonly lastUpdated: Date;
  constructor(
    public readonly id: string,
    public readonly projectId: string,
    public readonly companyId: string,
    public readonly questionSetId: string,
    public readonly assessorId: string | null,
    public readonly name: string | null,
    public readonly examAt: Date,
    public readonly startedAt: Date | null,
    public readonly finishedAt: Date | null,
    public readonly scoringOverride: CategoryScoringMap | null,
    public readonly photoA: string | null,
    public readonly photoB: string | null,
    public readonly photoC: string | null,
    public readonly photoD: string | null,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
    public readonly archivedAt: Date | null,
  ) {
    if (this.archivedAt !== null) {
      this.status = "Gearchiveerd";
    } else if (this.finishedAt !== null) {
      this.status = "Afgerond";
    } else if (this.startedAt === null) {
      this.status = "Concept";
    } else {
      this.status = "In uitvoering";
    }

    this.lastUpdated = new Date(
      Math.max(
        this.createdAt.getTime(),
        this.updatedAt.getTime(),
        startedAt?.getTime() ?? 0,
        finishedAt?.getTime() ?? 0,
      ),
    );
  }

  static fromJson(json: any): Exam {
    return new Exam(
      json["id"],
      json["project_id"],
      json["company_id"],
      json["question_set_id"],
      json["assessor_id"] ?? null,
      json["name"] ?? null,
      new Date(json["exam_at"]),
      json["started_at"] ? new Date(json["started_at"]) : null,
      json["finished_at"] ? new Date(json["finished_at"]) : null,
      json["override_category_scores"]?.["scoring_per_score_category"] ?? null,
      json["photo_a"] ?? null,
      json["photo_b"] ?? null,
      json["photo_c"] ?? null,
      json["photo_d"] ?? null,
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
      json["archived_at"] ? new Date(json["archived_at"]) : null,
    );
  }
}

export class ExamParticipant {
  constructor(
    public readonly id: string,
    public readonly examId: string,
    public readonly contractor: string,
    public readonly subContractor: string,
    public readonly name: string,
    public readonly position: number,
    public readonly noShow: boolean,
    public readonly badgeNumber: string | null,
    public readonly nationality: string | null,
    public readonly language: string | null,
    public readonly certification: string | null,
    public readonly note: string | null,
    public readonly score: string | null,
    public readonly scorePreviewData: { [category: string]: number } | null,
    public readonly recentAttempts: number | null,
    public readonly isAborted: boolean | null,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
    public readonly deletedAt: Date | null,
  ) {}

  static fromJson(json: any): ExamParticipant {
    return new ExamParticipant(
      json["id"],
      json["exam_id"],
      json["contractor"],
      json["subcontractor"],
      json["name"],
      json["position"],
      json["no_show"] ?? false,
      json["badge_number"] ?? null,
      json["nationality"] ?? null,
      json["language"] ?? null,
      json["certification"] ?? null,
      json["note"] ?? null,
      json["score_preview"] ?? null,
      json["score_preview_data"] ?? null,
      json["recent_attempts"] ?? null,
      json["is_aborted"] ?? null,
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
      json["deleted_at"] ? new Date(json["deleted_at"]) : null,
    );
  }
}

export class ProposedChange {
  constructor(
    public readonly action: string,
    public readonly idName: string,
    public readonly oldValue: string | null,
    public readonly newValue: string,
  ) {}

  static fromJson(json: any): ProposedChange {
    return new ProposedChange(
      json["action"],
      json["id_name"],
      json["old_value"],
      json["new_value"],
    );
  }
}

export class Role {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly description: string,
  ) {}

  static fromJson(json: any): Role {
    return new Role(json["id"], json["name"], json["description"]);
  }
}

export class RealmRole {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly description: string,
    public readonly composite: boolean,
    public readonly clientRole: boolean,
    public readonly containerId: string,
  ) {}

  static fromJson(json: any): RealmRole {
    return new RealmRole(
      json["id"],
      json["name"],
      json["description"],
      json["composite"],
      json["clientRole"],
      json["containerId"],
    );
  }
}

export class Certificate {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly contractor: string,
    public readonly project: string,
    public readonly company: string,
    public readonly questionSet: string,
    public readonly questionSetId: string | null,
    public readonly testDate: Date,
    public readonly score: number,
    public readonly scoreClassification: string,
    public readonly downloadFileName: string,
    public readonly examId: string,
    public readonly participantId: string,
  ) {}

  get formattedTestDate(): string {
    return moment(this.testDate).format("DD-MM-YYYY HH:mm");
  }

  static fromJson(json: any): Certificate {
    return new Certificate(
      json["id"],
      json["name"],
      json["contractor"],
      json["project"],
      json["company"],
      json["question_set"],
      json["question_set_id"] ? json["question_set_id"] : null,
      new Date(json["test_date"]),
      json["score"],
      json["score_classification"],
      json["download_file_name"],
      json["exam_id"],
      json["participant_id"],
    );
  }
}

export class WhitelistedEmail {
  constructor(
    public readonly id: string,
    public readonly email: string,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): WhitelistedEmail {
    return new WhitelistedEmail(
      json["id"],
      json["email"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export class CertificateType {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly question_set_id: string,
    public readonly is_active: boolean,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): CertificateType {
    return new CertificateType(
      json["id"],
      json["name"],
      json["question_set_id"],
      json["is_active"],
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}

export class DefaultInspectionAttachment {
  constructor(
    public readonly id: string,
    public readonly path: string,
    public readonly name: string,
    public readonly order: number,
    public readonly isActive: boolean,
    public readonly fromDate: Date,
    public readonly toDate: Date,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): DefaultInspectionAttachment {
    return new DefaultInspectionAttachment(
      json["id"],
      json["path"],
      json["name"],
      json["order"],
      json["is_active"],
      new Date(json["from_date"]),
      new Date(json["to_date"]),
      new Date(json["created_at"]),
      new Date(json["updated_at"]),
    );
  }
}
